import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import OnboardingTermLengthModal from '../Onboarding/TermLengthPage';
import OnboardingDueDateModal from '../Onboarding/DueDatePage';
import OnboardingRentAmountModal from '../Onboarding/RentAmountPage';
import AccountInfoPage from '../Onboarding/AccountInfoPage';
import InviteTenantPage from '../Onboarding/InviteTenantPage';
import TaxVerificationPage from '../Onboarding/TaxVerificationPage';
import PhoneVerificationPage from '../Onboarding/PhoneVerificationPage';
import OnboardingConfirmationPage from '../Onboarding/ConfirmationPage';
import OnboardingLeaseSelectorPage from '../Onboarding/LeaseSelectorPage';
import OnboardingEmptyLeasePage from '../Onboarding/EmptyLeasePage';
import OnboardingAreYouSurePage from '../Onboarding/RentalProtectionAreYouSurePage';
import OnboardingUnsignedLeaseWarning from '../Onboarding/RentalProtectionUnsignedLeaseWarning';
import OnboardingIneligibleLeaseSelected from '../Onboarding/RentalProtectionIneligibleLeaseSelected';
import OnboardingDifferentPaymentTermThanLeaseWarning from '../Onboarding/RentalProtectionDifferentTermWarning';
import OnboardingDifferentValueThanLeaseWarning from '../Onboarding/RentalProtectionDifferentValueWarning';
import ExitPaymentSetup from '../Onboarding/ExitPaymentSetup';
import PostConfirmationPage from '../Onboarding/PostConfirmationPage';
import EmptyLeasePage from '../Dashboard/SetupNewTermModal/modules/RentalProtection/EmptyLease.container';
import LeaseSelectorPage from '../Dashboard/SetupNewTermModal/modules/RentalProtection/LeaseSelector.container';
import AreYouSurePage from '../Dashboard/SetupNewTermModal/modules/RentalProtection/AreYouSure.container';
import IneligibleLeaseSelected from '../Dashboard/SetupNewTermModal/modules/RentalProtection/IneligibleLeaseSelected.container';
import UnsignedLeaseWarning from '../Dashboard/SetupNewTermModal/modules/RentalProtection/UnsignedLeaseWarning.container';
import DifferentPaymentTermThanLeaseWarning from '../Dashboard/SetupNewTermModal/modules/RentalProtection/DifferentPaymentTermThanLeaseWarning.container';
import DifferentValueThanLeaseWarning from '../Dashboard/SetupNewTermModal/modules/RentalProtection/DifferentValueThanLeaseWarning.container';
import InviteTenantSetupNewTerm from '../Dashboard/SetupNewTermModal/modules/RentalProtection/InviteTenant.container';
import DueDateSetupNewTerm from '../Dashboard/SetupNewTermModal/modules/RentalProtection/DueDate.container';
import TermLengthSetupNewTerm from '../Dashboard/SetupNewTermModal/modules/RentalProtection/TermLength.container';
import RentAmountSetupNewTerm from '../Dashboard/SetupNewTermModal/modules/RentalProtection/RentAmount.container';
import EditRentAmountModal from './EditRentAmountModal';
import EditDueDateModal from './EditDueDateModal';
import EditTermLengthModal from './EditTermLengthModal';
import KnowledgeBasedAuthenticationPage from '@rental-manager/pages/Onboarding/KnowledgeBasedAuthenticationPage';
import EditPaymentLengthModal from './EditPaymentLengthModal';
import EditTenantInfoModal from './EditTenantInfoModal';
import EditStripeContactInfoModal from './EditStripeContactInfo';
import StripeVerificationModal from './StripeVerificationModal';
import StopPaymentsFeedbackModal from './StopPaymentsFeedbackModal';
import OneTimePaymentModal from './OneTimePaymentModalV2';
import CancelMoveInCostsModal from './CancelMoveInCostsModal';
import MoveInCostsPage from '../Onboarding/MoveInCostsPage';
import ConfirmMoveInCostsPage from './ConfirmMoveInCostsPage';
import SetupNewTermModal from './SetupNewTermModal';
import HowToSetupPaymentPlanModal from './PaymentPlanHowToSetup';
import HandleMissedRentModal from './PaymentPlanHandleMissedRent';
import paths from '@rental-manager/pathsManifest';
import { PROPERTY_STATUS } from '@rental-manager/constants/OnboardingConstants';
import type { IClusterSettingsModel, IConfigModel } from '@rental-manager/models/Types';
import { ManagerRouteWithAuth as RouteWithAuth } from '@rental-manager/auth/ManagerRouteWithAuth';
import PaymentPlanInstallmentRequests from './PaymentPlanInstallmentRequests';
import type { Location } from 'history';

type Props = {
  showTermEndedRoutes: boolean;
  nextTermPropertyStatus: string;
  baseUrl: string;
  listing: Record<string, unknown>;
  propertyId: string;
  config: IConfigModel;
  landlordStatus: string;
  clusterSettings: IClusterSettingsModel;
  canCreateOneTimePaymentRequest: boolean;
  location: Location;
};

class DashboardModalRoutes extends React.Component<Props> {
  static defaultProps = {
    showTermEndedRoutes: false,
    nextTermPropertyStatus: '',
  };

  modalProps: {
    baseUrl: string;
    listing: Record<string, unknown>;
    propertyId: string;
    landlordStatus: string;
    clusterSettings: IClusterSettingsModel;
  };

  constructor(props) {
    super(props);
    this.modalProps = {
      baseUrl: props.baseUrl,
      listing: props.listing,
      propertyId: props.propertyId,
      landlordStatus: props.landlordStatus,
      clusterSettings: props.clusterSettings,
    };
  }

  EditAccountInfoRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editAccountInfoPage}
      exact
      render={(routeProps) => {
        return <AccountInfoPage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditPhoneVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.phoneVerificationPage}
      exact
      render={(routeProps) => {
        return <PhoneVerificationPage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditRentAmountRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editRentAmountPage}
      exact
      render={(routeProps) => {
        return <EditRentAmountModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditDueDateRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editDueDatePage}
      exact
      render={(routeProps) => {
        return <EditDueDateModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditTermLengthRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editTermLengthPage}
      exact
      render={(routeProps) => {
        return <EditTermLengthModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditPaymentLengthRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editPaymentLengthPage}
      exact
      render={(routeProps) => {
        return <EditPaymentLengthModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditTenantInfoRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editTenantInfoPage}
      exact
      render={(routeProps) => {
        return <EditTenantInfoModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EditStripeContactInfoRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.editStripeContactInfo}
      exact
      render={(routeProps) => {
        return <EditStripeContactInfoModal {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  StopPaymentsFeedbackRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.stopPaymentsFeedbackPage}
      exact
      render={(routeProps) => {
        return <StopPaymentsFeedbackModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  OneTimePaymentRoute = (): React.ReactNode => {
    const { canCreateOneTimePaymentRequest } = this.props;
    return (
      canCreateOneTimePaymentRequest && (
        <RouteWithAuth
          path={this.props.baseUrl + paths.dashboard.createOneTimePaymentRequestPage}
          exact
          render={(routeProps) => {
            return <OneTimePaymentModal {...routeProps} {...this.modalProps} fromDashboard />;
          }}
        />
      )
    );
  };

  CancelMoveInCostsRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.cancelMoveInCostsRequest}
      exact
      render={(routeProps) => {
        return <CancelMoveInCostsModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  MoveInCostsRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.createMoveInCostsPage}
      exact
      render={(routeProps) => {
        return <MoveInCostsPage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  ConfirmMoveInCostsRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.confirmMoveInCostsPage}
      exact
      render={(routeProps) => {
        return <ConfirmMoveInCostsPage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  KnowledgeBasedAuthenticationRouteForMIC = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage}
      exact
      render={(routeProps) => {
        return <KnowledgeBasedAuthenticationPage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  StripeVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.stripeVerificationPage}
      exact
      render={(routeProps) => {
        return <StripeVerificationModal {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  EmergencyTaxVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.taxVerificationPage}
      exact
      render={(routeProps) => {
        return <TaxVerificationPage {...routeProps} {...this.modalProps} emergencyMode />;
      }}
    />
  );

  PostConfirmationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.postConfirmationPage}
      exact
      render={(routeProps) => {
        return <PostConfirmationPage {...routeProps} {...this.modalProps} setupNewTerm />;
      }}
    />
  );

  SetupNewTermRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.setupNewTermPage}
      exact
      render={(routeProps) => {
        return <SetupNewTermModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  EmptyLeaseRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.emptyLeasePage}
      exact
      render={(routeProps) => {
        return <EmptyLeasePage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  LeaseSelectorRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.leaseSelectorPage}
      exact
      render={(routeProps) => {
        return <LeaseSelectorPage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  RentalProtectionAreYouSureRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.areYouSurePage}
      exact
      render={(routeProps) => {
        return <AreYouSurePage {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  RentalProtectionIneligibleLeaseSelectedRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.ineligibleLeaseSelected}
      exact
      render={(routeProps) => {
        return <IneligibleLeaseSelected {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  RentalProtectionUnsignedLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.unsignedLeaseWarning}
      exact
      render={(routeProps) => {
        return <UnsignedLeaseWarning {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  RentalProtectionDifferentValueThanLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.differentValueThanLeaseWarningPage}
      exact
      render={(routeProps) => {
        return <DifferentValueThanLeaseWarning {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionDifferentTermThanLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.differentTermThanLeaseWarningPage}
      exact
      render={(routeProps) => {
        return <DifferentPaymentTermThanLeaseWarning {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  DueDateSetupNewTermRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.dueDatePage}
      exact
      render={(routeProps) => {
        return <DueDateSetupNewTerm {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  InviteTenantSetupNewTermRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.inviteTenantPage}
      exact
      render={(routeProps) => {
        return <InviteTenantSetupNewTerm {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  TermLengthSetupNewTermRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.termLengthPage}
      exact
      render={(routeProps) => {
        return <TermLengthSetupNewTerm {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentAmountSetupNewTermRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.rentAmountPage}
      exact
      render={(routeProps) => {
        return <RentAmountSetupNewTerm {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  HowToSetupPaymentPlanModal = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.howToSetupPaymentPlan}
      exact
      render={(routeProps) => {
        return <HowToSetupPaymentPlanModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  HandleMissedRentModal = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.handleMissedRent}
      exact
      render={(routeProps) => {
        return <HandleMissedRentModal {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  PaymentPlanInstallmentRequests = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.dashboard.PaymentPlanInstallmentRequests}
      exact
      render={(routeProps) => {
        return <PaymentPlanInstallmentRequests {...routeProps} {...this.modalProps} fromDashboard />;
      }}
    />
  );

  CatchAllRoute = (): React.ReactNode => (
    <Route
      path="*"
      render={(routeProps) => {
        if (routeProps.location.pathname !== this.props.baseUrl) {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }
        // Redirect to login from the dashboard if necessary
        return <></>;
      }}
    />
  );

  renderTermEndedRoutes = (): React.ReactNode => {
    const { nextTermPropertyStatus } = this.props;
    const isOnboarded = nextTermPropertyStatus === PROPERTY_STATUS.TENANT_INFO_SET;
    return (
      <Switch location={this.props.location}>
        {this.EditPhoneVerificationRoute()}
        {this.EditAccountInfoRoute()}
        {/* Tenant onboarded */}
        {this.EditPaymentLengthRoute()}
        {/* Tenant not yet onboarded */}
        {this.EditTermLengthRoute()}
        {this.EditDueDateRoute()}
        {this.EditRentAmountRoute()}
        {this.EditTenantInfoRoute()}
        {this.EditStripeContactInfoRoute()}
        {/* Edit Routes from dashboard for MIC */}
        {this.CancelMoveInCostsRoute()}
        {this.MoveInCostsRoute()}
        {isOnboarded && this.KnowledgeBasedAuthenticationRouteForMIC()}
        {this.ConfirmMoveInCostsRoute()}
        {/* MIC Route for Invite Tenant Flow */}
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.moveInCostsPage}
          exact
          render={(routeProps) => {
            return <MoveInCostsPage {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage}
          exact
          render={(routeProps) => {
            return <KnowledgeBasedAuthenticationPage {...routeProps} {...this.modalProps} />;
          }}
        />
        {this.StripeVerificationRoute()}
        {this.OneTimePaymentRoute()}
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.leaseSelectorPage}
          exact
          render={(routeProps) => {
            return <OnboardingLeaseSelectorPage {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.emptyLeasePage}
          exact
          render={(routeProps) => {
            return <OnboardingEmptyLeasePage {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.unsignedLeaseWarning}
          exact
          render={(routeProps) => {
            return <OnboardingUnsignedLeaseWarning {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.ineligibleLeaseSelected}
          exact
          render={(routeProps) => {
            return <OnboardingIneligibleLeaseSelected {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.differentTermThanLeaseWarningPage}
          exact
          render={(routeProps) => {
            return <OnboardingDifferentPaymentTermThanLeaseWarning {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.differentValueThanLeaseWarningPage}
          exact
          render={(routeProps) => {
            return <OnboardingDifferentValueThanLeaseWarning {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.areYouSurePage}
          exact
          render={(routeProps) => {
            return <OnboardingAreYouSurePage {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.exitPaymentSetup}
          exact
          render={(routeProps) => {
            return <ExitPaymentSetup {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.rentAmountPage}
          exact
          render={(routeProps) => {
            return <OnboardingRentAmountModal {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.dueDatePage}
          exact
          render={(routeProps) => {
            return <OnboardingDueDateModal {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.termLengthPage}
          exact
          render={(routeProps) => {
            return <OnboardingTermLengthModal {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.inviteTenantPage}
          exact
          render={(routeProps) => {
            return <InviteTenantPage {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.confirmationPage}
          exact
          render={(routeProps) => {
            return <OnboardingConfirmationPage {...routeProps} {...this.modalProps} />;
          }}
        />
        <RouteWithAuth
          path={this.props.baseUrl + paths.onboarding.postConfirmationPage}
          exact
          render={(routeProps) => {
            return <PostConfirmationPage {...routeProps} {...this.modalProps} />;
          }}
        />

        <RouteWithAuth
          path={this.props.baseUrl + paths.dashboard.setupNewTermPage}
          exact
          render={(routeProps) => {
            return <SetupNewTermModal {...routeProps} {...this.modalProps} />;
          }}
        />
        {this.PostConfirmationRoute()}
        {this.StopPaymentsFeedbackRoute()}
        {this.CatchAllRoute()}
      </Switch>
    );
  };

  renderDashboardRoutes = (): React.ReactNode => {
    return (
      <Switch location={this.props.location}>
        {this.EditPhoneVerificationRoute()}
        {this.EditDueDateRoute()}
        {/* Tenant onboarded */}
        {this.EditPaymentLengthRoute()}
        {/* Tenant not yet onboarded */}
        {this.EditTermLengthRoute()}
        {this.StopPaymentsFeedbackRoute()}
        {this.EditAccountInfoRoute()}
        {this.EditRentAmountRoute()}
        {this.EditTenantInfoRoute()}
        {this.EditStripeContactInfoRoute()}
        {this.StripeVerificationRoute()}
        {this.EmergencyTaxVerificationRoute()}
        {this.OneTimePaymentRoute()}
        {this.CancelMoveInCostsRoute()}
        {this.MoveInCostsRoute()}
        {this.KnowledgeBasedAuthenticationRouteForMIC()}
        {this.ConfirmMoveInCostsRoute()}
        {this.SetupNewTermRoute()}
        {this.EmptyLeaseRoute()}
        {this.LeaseSelectorRoute()}
        {this.RentalProtectionAreYouSureRoute()}
        {this.RentalProtectionIneligibleLeaseSelectedRoute()}
        {this.RentalProtectionUnsignedLeaseWarningRoute()}
        {this.RentalProtectionDifferentValueThanLeaseWarningRoute()}
        {this.RentalProtectionDifferentTermThanLeaseWarningRoute()}
        {this.InviteTenantSetupNewTermRoute()}
        {this.DueDateSetupNewTermRoute()}
        {this.TermLengthSetupNewTermRoute()}
        {this.RentAmountSetupNewTermRoute()}
        {this.PostConfirmationRoute()}
        {this.HowToSetupPaymentPlanModal()}
        {this.HandleMissedRentModal()}
        {this.PaymentPlanInstallmentRequests()}
        {this.CatchAllRoute()}
      </Switch>
    );
  };

  render(): React.ReactNode {
    const { showTermEndedRoutes } = this.props;

    if (showTermEndedRoutes) {
      return this.renderTermEndedRoutes();
    }
    return this.renderDashboardRoutes();
  }
}

export default connect(({ router }: any) => ({ location: router.location }))(DashboardModalRoutes);
